// Presentation manifest for the role-scoped shell (SENSARRA-VERTICAL-TENANT-SURFACE-1).
//
// This file is PRESENTATION ONLY — it maps a surface id to its nav label + path.
// It carries NO access rule. The access decision (which surfaces a caller may
// see) is the backend's, delivered as `visible_surfaces` from GET /api/v1/me/surfaces
// and consumed via `useSurfaces`. Nav rendering and route guards both read that
// one list, so they cannot drift.
//
// Surface ids MUST match the backend `_SURFACE_MIN_ROLE` keys in
// backend/app/services/surface_service.py.

export interface NavSurface {
  id: string;
  path: string;
  label: string;
}

// Ordered left→right. Customer-safe surfaces lead (Connections is the customer
// HOME), then operator/internal, then the Director console. A customer sees only
// the leading customer-safe block; the rest are filtered out by `visible_surfaces`.
export const NAV_SURFACES: NavSurface[] = [
  // The `research` surface is a connector/credential-CONFIG page (the Sensarra
  // Connections surface). It is SUBSTRATE config, not the Research Facility
  // workspace product — so the tab is labeled "Connections" to avoid overstating
  // it as a research workspace. (The route id/path stay `research` for now.)
  { id: 'research', path: '/research', label: 'Connections' },
  { id: 'chat', path: '/chat', label: 'Chat' },
  { id: 'skills', path: '/skills', label: 'Skills' },
  { id: 'sources', path: '/sources', label: 'Sources' },
  { id: 'practice_admin', path: '/admin/practices', label: 'Practice Admin' },
  { id: 'inbox', path: '/inbox', label: 'Inbox' },
  { id: 'dashboard', path: '/dashboard', label: 'Dashboard' },
  { id: 'board', path: '/board', label: 'Board' },
  { id: 'workbench', path: '/workbench', label: 'Workbench' },
  { id: 'talents', path: '/talents', label: 'Talents' },
  { id: 'vertical_admin', path: '/admin/verticals', label: 'Vertical Admin' },
  { id: 'settings', path: '/settings', label: 'Settings' },
  { id: 'command', path: '/command', label: 'Command' },
];

// The customer-safe surface ids — the fail-closed default for any caller who is
// NOT a server-confirmed Director, used until the authoritative `visible_surfaces`
// resolves. Keep in sync with the tenant-min-role rows in the backend map.
export const CUSTOMER_SAFE_SURFACE_IDS: string[] = [
  'research',
  'chat',
  'skills',
  'sources',
  'settings',
];

// Every surface id the shell knows about — the fast-path default for a
// server-confirmed Director (avoids a nav flash while /me/surfaces loads). A
// non-Director never gets this set; the backend remains the real gate.
export const ALL_SURFACE_IDS: string[] = [
  'research',
  'chat',
  'skills',
  'sources',
  'practice_admin',
  'settings',
  'inbox',
  'dashboard',
  'board',
  'workbench',
  'talents',
  'vertical_admin',
  'command',
  'tenant_switcher',
  'settings.agents',
  'settings.runtime',
  'settings.models.custom_credentials',
  'settings.models.diagnostics',
];

// Operator/internal Settings sub-panels, keyed to their backend surface id
// (SENSARRA-VERTICAL-TENANT-SURFACE-1 §7.1). The settings route mounts panels with
// no gate of its own, so a plain tenant could otherwise reach /settings/agents,
// /settings/runtime, and the vault-credentials tab. A panel NOT listed here is
// customer-safe and always shown.
export const OPERATOR_SETTINGS_PANELS: Record<string, string> = {
  agents: 'settings.agents',
  runtime: 'settings.runtime',
};

/** True if this settings sub-panel is visible to the caller (customer-safe panels
 *  always are; operator/internal panels require their backend surface). */
export function isSettingsPanelVisible(panelId: string, surfaces: Set<string>): boolean {
  const required = OPERATOR_SETTINGS_PANELS[panelId];
  return required ? surfaces.has(required) : true;
}

// The caller's shell "home". Operators/Director land on the dashboard; a customer
// (who cannot see the dashboard) lands on Research. Drives index + denied-route
// redirects so nobody is ever bounced onto a surface they can't see.
export function homePathFor(surfaces: Set<string>): string {
  return surfaces.has('dashboard') ? '/dashboard' : '/research';
}
